import { protectWithWorkspacePermissions } from '@routes/-protectWithPermission'
import { withCompanyName } from '@routes/-withCompanyName'
import { createFileRoute, Outlet, redirect } from '@tanstack/react-router'
import { MainColumn } from 'features/commonUI'
import { FRAUD_ANALYSIS_TITLE, FraudScoreUpgradeView, getFraudScoreEntitlement } from 'features/fraudAnalysis'
import { getLDFlag, identifyLDSubscription } from 'helpers/launchDarkly'
import { subscriptionQueryOptions } from 'hooks/api/subscriptions'
import { canViewAnalyticsPermission } from 'hooks/usePermission'
import { SubscriptionStatus, SubscriptionType } from 'models'

export const Route = createFileRoute('/_authenticated/_layout/workspaces/$subscriptionId/fraud-analysis')({
  component: FraudAnalysisLayout,
  staticData: { rumClickTracking: true },
  head: () => ({
    meta: [
      {
        title: withCompanyName(FRAUD_ANALYSIS_TITLE),
      },
    ],
  }),
  beforeLoad: async (ctx) => {
    await protectWithWorkspacePermissions(canViewAnalyticsPermission)(ctx)

    if (ctx.cause !== 'preload') {
      await identifyLDSubscription(ctx.params.subscriptionId, ctx.context.ldClient)
    }

    if (!getLDFlag('dashboard.fsml.visible', false, ctx.context.ldClient)) {
      throw redirect({ to: '/', replace: true })
    }

    const fraudScoreEntitlement = await getFraudScoreEntitlement(
      ctx.context.queryClient,
      ctx.context.buildRequest,
      ctx.params.subscriptionId
    )

    let showInterest = false
    if (fraudScoreEntitlement === 'disabled') {
      const subscription = await ctx.context.queryClient.ensureQueryData(
        subscriptionQueryOptions(ctx.context.buildRequest, ctx.params.subscriptionId, [])
      )
      showInterest =
        subscription.type !== SubscriptionType.Prepaid &&
        subscription.type !== SubscriptionType.ProofOfConcept &&
        subscription.status !== SubscriptionStatus.ProofOfConcept
    }

    return { fraudScoreEntitlement, showInterest }
  },
})

function FraudAnalysisLayout() {
  const { subscriptionId } = Route.useParams()
  const { fraudScoreEntitlement, showInterest } = Route.useRouteContext()

  if (fraudScoreEntitlement === 'disabled') {
    return <FraudScoreUpgradeView subscriptionId={subscriptionId} showInterest={showInterest} />
  }

  return (
    <MainColumn>
      <Outlet />
    </MainColumn>
  )
}
